import React, { FC } from 'react';
import * as yup from 'yup';
import { Form, Formik } from 'formik';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { maskCpf, maskCnpj } from 'utils/banking';
import { cpf, cnpj } from 'cpf-cnpj-validator';
import { EPrePartnerType, IPrePartner } from 'common/create-banking-account.dto';

interface IProps {
  open: boolean;
  onClose: () => void;
  onAdd: (partner: IPrePartner) => void;
}

const validationSchema = yup.object({
  type: yup.string().required('Campo obrigatório'),

  name: yup.string().when('type', {
    is: EPrePartnerType.PHYSICAL,
    then: (schema) =>
      schema.required('Campo obrigatório').test('hasSurname', 'Informe o nome completo do representante, incluindo sobrenome.', (value) => {
        if (!value) return false;
        return value.trim().split(/\s+/).length > 1;
      }),
    otherwise: (schema) => schema.optional(),
  }),

  companyName: yup.string().when('type', {
    is: EPrePartnerType.LEGAL,
    then: (schema) => schema.required('Campo obrigatório'),
    otherwise: (schema) => schema.optional(),
  }),

  document: yup.string().when('type', {
    is: EPrePartnerType.PHYSICAL,
    then: (schema) =>
      schema.required('Campo obrigatório').test('cpfValid', 'CPF inválido', (value) => {
        if (!value) return false;
        return cpf.isValid(value.replace(/\D/g, ''));
      }),
    otherwise: (schema) => schema.optional(),
  }),

  cnpj: yup.string().when('type', {
    is: EPrePartnerType.LEGAL,
    then: (schema) =>
      schema.required('Campo obrigatório').test('cnpjValid', 'CNPJ inválido', (value) => {
        if (!value) return false;
        return cnpj.isValid(value.replace(/\D/g, ''));
      }),
    otherwise: (schema) => schema.optional(),
  }),

  participation: yup
    .string()
    .required('Campo obrigatório')
    .matches(/^\d+(\.\d{1,2})?%$/, 'Deve ser um valor percentual, ex: 50%'),

  legalRepresentative: yup.boolean(),
});

const AddPartnerModal: FC<IProps> = ({ open, onClose, onAdd }) => {
  const initialValues: IPrePartner = {
    type: EPrePartnerType.PHYSICAL,
    document: '',
    cnpj: '',
    name: '',
    companyName: '',
    participation: '',
    responsible: false,
    legalRepresentative: false,
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 600, fontSize: 20, mt: 2 }}>Cadastro de sócios</DialogTitle>

      <DialogContent>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async (values, { resetForm }) => {
            onAdd(values);
            resetForm();
            onClose();
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, setFieldValue, resetForm, isValid }) => (
            <Form>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Box>
                  <Typography fontWeight={500} fontSize={16}>
                    Qual é o tipo de pessoa?
                  </Typography>
                  <FormControl>
                    <RadioGroup
                      value={values.type}
                      onChange={(_, value) =>
                        resetForm({
                          values: {
                            ...initialValues,
                            type: value as EPrePartnerType,
                          },
                        })
                      }
                      row
                    >
                      <FormControlLabel value={EPrePartnerType.PHYSICAL} control={<Radio />} label="Pessoa Física" />
                      <FormControlLabel value={EPrePartnerType.LEGAL} control={<Radio />} label="Pessoa Jurídica" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {values.type === EPrePartnerType.PHYSICAL ? (
                  <TextField
                    label="Nome completo"
                    placeholder="Nome do responsável"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    fullWidth
                    variant="standard"
                    InputLabelProps={{ shrink: true, sx: { fontWeight: 500 } }}
                  />
                ) : (
                  <TextField
                    label="Razão Social"
                    placeholder="Nome da empresa"
                    name="companyName"
                    value={values.companyName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.companyName && Boolean(errors.companyName)}
                    helperText={touched.companyName && errors.companyName}
                    fullWidth
                    variant="standard"
                    InputLabelProps={{ shrink: true, sx: { fontWeight: 500 } }}
                  />
                )}

                {values.type === EPrePartnerType.PHYSICAL ? (
                  <TextField
                    label="CPF"
                    placeholder="Ex: 000.000.000-00"
                    name="document"
                    value={values.document}
                    onChange={(e) => {
                      let input = e.target.value;
                      if (input.length > 14) input = input.slice(0, 14);
                      setFieldValue('document', maskCpf(input));
                    }}
                    onBlur={handleBlur}
                    error={touched.document && Boolean(errors.document)}
                    helperText={touched.document && errors.document}
                    fullWidth
                    variant="standard"
                    InputLabelProps={{ shrink: true, sx: { fontWeight: 500 } }}
                  />
                ) : (
                  <TextField
                    label="CNPJ"
                    placeholder="Ex: 00.000.000/0000-00"
                    name="cnpj"
                    value={values.cnpj}
                    onChange={(e) => {
                      let input = e.target.value;
                      if (input.length > 18) input = input.slice(0, 18);
                      setFieldValue('cnpj', maskCnpj(input));
                    }}
                    onBlur={handleBlur}
                    error={touched.cnpj && Boolean(errors.cnpj)}
                    helperText={touched.cnpj && errors.cnpj}
                    fullWidth
                    variant="standard"
                    InputLabelProps={{ shrink: true, sx: { fontWeight: 500 } }}
                  />
                )}

                <TextField
                  label="Percentual de participação na empresa"
                  placeholder="25%"
                  name="participation"
                  value={values.participation}
                  onChange={(e) => {
                    let val = e.target.value.replace(/\D/g, '');

                    if (val) {
                      let intVal = parseInt(val, 10);
                      if (intVal > 100) intVal = 100;
                      val = intVal.toString();
                    }

                    setFieldValue('participation', val ? `${val}%` : '');
                  }}
                  onBlur={handleBlur}
                  error={touched.participation && Boolean(errors.participation)}
                  helperText={touched.participation && errors.participation}
                  fullWidth
                  variant="standard"
                  InputLabelProps={{ shrink: true, sx: { fontWeight: 500 } }}
                />

                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Button onClick={onClose} variant="outlined" color="primary" fullWidth sx={{ borderRadius: 24 }}>
                    Cancelar
                  </Button>

                  <Button disabled={!isValid} type="submit" variant="contained" color="primary" fullWidth sx={{ borderRadius: 24 }}>
                    Cadastrar sócio
                  </Button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default AddPartnerModal;
